import type { CSSProperties } from 'react';

type StarVariant = 'summary' | 'feed';

interface StarRatingProps {
  score: number;
  size?: number;
  maxStars?: number;
  variant?: StarVariant;
  className?: string;
  style?: CSSProperties;
}

const getStarColor = (variant: StarVariant) => {
  if (variant === 'summary') {
    return 'var(--product-reviews-star-color, var(--product-reviews-feed-star-color, var(--accent)))';
  }
  return 'var(--product-reviews-feed-star-color, var(--product-reviews-star-color, var(--accent)))';
};

const FilledStar = ({ size, variant }: { size: number; variant: StarVariant }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
      fill={getStarColor(variant)}
    />
  </svg>
);

const getEmptyStarColor = (variant: StarVariant) => {
  const defaultColor = 'color-mix(in srgb, var(--text-light) 30%, transparent)';
  if (variant === 'summary') {
    return `var(--product-reviews-empty-star-color, var(--product-reviews-feed-empty-star-color, ${defaultColor}))`;
  }
  return `var(--product-reviews-feed-empty-star-color, var(--product-reviews-empty-star-color, ${defaultColor}))`;
};

const EmptyStar = ({ size, variant }: { size: number; variant: StarVariant }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
      fill={getEmptyStarColor(variant)}
    />
  </svg>
);

const HalfStar = ({ size, variant }: { size: number; variant: StarVariant }) => (
  <div style={{ position: 'relative', width: size, height: size }}>
    <EmptyStar size={size} variant={variant} />
    <div style={{ position: 'absolute', top: 0, left: 0, width: '50%', overflow: 'hidden' }}>
      <FilledStar size={size} variant={variant} />
    </div>
  </div>
);

export function StarRating({ score, size = 16, maxStars = 5, variant = 'feed', className, style }: StarRatingProps) {
  const fullStars = Math.floor(score);
  const hasHalfStar = score - fullStars >= 0.5;
  const emptyStars = maxStars - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className={className} style={{ display: 'flex', gap: '4px', ...style }}>
      {Array.from({ length: fullStars }).map((_, i) => (
        <FilledStar key={`filled-${i}`} size={size} variant={variant} />
      ))}
      {hasHalfStar && <HalfStar key="half" size={size} variant={variant} />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <EmptyStar key={`empty-${i}`} size={size} variant={variant} />
      ))}
    </div>
  );
}
